import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Stack,
  Chip,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Divider,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as DenyIcon,
  CalendarMonth as RecheckIcon,
  LocalPharmacy as DispenseIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import { refillRequestService, RxRefillRequest } from '../../services/refillRequestService';
import { appointmentApi } from '../../utils/api';
import { formatCalendarDate, INSTANT_FORMATS } from '../../utils/dateUtils';
import { useFormatInstant } from '../../hooks/useClinicTimezone';

interface Props {
  open: boolean;
  onClose: () => void;
  request: RxRefillRequest;
  onDecided?: () => void;
}

type DecisionAction = 'approve' | 'deny' | 'recheck';

/** The next-visit pattern this dialog has always shown — wall-clock tokens only. */
const NEXT_VISIT_FORMAT = 'EEE MMM d, h:mm a';

const RefillRequestDecisionDialog: React.FC<Props> = ({ open, onClose, request, onDecided }) => {
  const { enqueueSnackbar } = useSnackbar();
  // Two INSTANTS on this screen: RxRefillRequest.createdAt (@default(now()),
  // DATE-KINDS §1.4) and Appointment.date (a real zone-aware conversion in
  // appointments.controller.ts, §1.3). Render both in the CLINIC's zone. The
  // expiry chip below is the opposite kind — see the comment there.
  const formatInstant = useFormatInstant();
  const [action, setAction] = useState<DecisionAction>('approve');
  const [decisionNotes, setDecisionNotes] = useState('');
  const [dispenseNow, setDispenseNow] = useState(true);
  const [quantity, setQuantity] = useState<string>('');
  const [unit, setUnit] = useState<string>('units');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const med = request.medication;
  const totalFillsAllowed = (med?.refillsAuthorized ?? 0) + 1;
  const fillsDispensed = med?._count?.dispenseEvents ?? 0;
  const fillsLeft = totalFillsAllowed - fillsDispensed;

  // Light appointment context — helps the vet decide whether to dispense
  // now, defer to a scheduled visit, or pick "Request recheck" for an
  // overdue patient. Only fetched when this dialog is open AND the request
  // has a linked horse.
  const horseId = request.horseId ?? undefined;
  const { data: upcomingAppointments = [] } = useQuery(
    ['appointments', 'upcoming', horseId],
    () => appointmentApi.list({ horseId, dateFrom: new Date().toISOString() }),
    { enabled: open && !!horseId, staleTime: 30_000 },
  );
  const nextAppointment = upcomingAppointments.length
    ? [...upcomingAppointments].sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
      )[0]
    : null;

  const handleSubmit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      if (action === 'approve') {
        const qty = dispenseNow ? parseFloat(quantity) : undefined;
        if (dispenseNow && (!qty || qty <= 0)) {
          setError('Enter a quantity to dispense, or uncheck "Dispense now".');
          setSubmitting(false);
          return;
        }
        await refillRequestService.approve(request.id, {
          decisionNotes: decisionNotes.trim() || undefined,
          dispenseQuantity: dispenseNow ? qty : undefined,
          dispenseUnit: dispenseNow ? unit.trim() : undefined,
        });
        enqueueSnackbar(
          dispenseNow ? 'Approved and dispensed' : 'Approved (no fill recorded)',
          { variant: 'success' },
        );
      } else if (action === 'deny') {
        await refillRequestService.deny(request.id, {
          decisionNotes: decisionNotes.trim() || undefined,
        });
        enqueueSnackbar('Refill denied', { variant: 'info' });
      } else {
        await refillRequestService.requestRecheck(request.id, {
          decisionNotes: decisionNotes.trim() || undefined,
        });
        enqueueSnackbar('Recheck requested', { variant: 'info' });
      }
      if (onDecided) onDecided();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to record decision');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Decide refill request</DialogTitle>
      <DialogContent>
        {/* Request context header */}
        <Box sx={{ mb: 2 }}>
          {med ? (
            <>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {med.name}
                {med.dosage && ` (${med.dosage})`}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 1 }}>
                {med.deaSchedule && (
                  <Chip size="small" label={`C-${med.deaSchedule}`} color="warning" />
                )}
                <Chip
                  size="small"
                  label={`Fills ${fillsDispensed} of ${totalFillsAllowed}`}
                  color={fillsLeft > 0 ? 'success' : 'default'}
                />
                {/* Medication.expirationDate is MIXED at the column level
                  * (DATE-KINDS §1.2) but CALENDAR on every path that reaches this
                  * chip — the Rx dialog's DatePicker, or startDate + 12 months in
                  * medications.service — which is why §2.2 lists this exact line as
                  * a formatCalendarDate swap. Parse the date PART and never re-zone:
                  * the old render read the UTC-midnight anchor in the viewer's zone
                  * and printed the PREVIOUS day in every US zone, i.e. a wrong expiry
                  * at exactly the boundary the vet is deciding against. */}
                {med.expirationDate && (
                  <Chip
                    size="small"
                    variant="outlined"
                    data-testid="refill-expires"
                    label={`Expires ${formatCalendarDate(med.expirationDate, 'M/d/yyyy')}`}
                  />
                )}
              </Stack>
            </>
          ) : (
            <Alert severity="warning">
              No medication linked. Verify this request first to pick the right Rx.
            </Alert>
          )}
          <Typography variant="body2" sx={{ mt: 1.5 }}>
            <strong>Patient:</strong> {request.horseName || '—'}
            {request.ownerName && (
              <>
                {' · '}<strong>Owner:</strong> {request.ownerName}
              </>
            )}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            via {request.channel.replace('_', ' ')} ·{' '}
            <span data-testid="refill-received-at">
              {formatInstant(request.createdAt, INSTANT_FORMATS.dateTimeNumeric)}
            </span>
          </Typography>
          {/* Assigned-vet chip — tells the deciding vet whether this request
           * was routed to them specifically (or to a colleague). Routing
           * hint only — any vet can still decide. */}
          {request.assignedVet && (
            <Box sx={{ mt: 1 }}>
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={`Assigned to ${request.assignedVet.name}`}
              />
            </Box>
          )}
          {/* Light visit context — informs the approve/recheck choice. */}
          {nextAppointment && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <EventIcon fontSize="small" sx={{ color: 'info.main' }} />
              <Typography variant="caption" color="text.secondary">
                Next visit:{' '}
                <span data-testid="refill-next-visit">
                  {formatInstant(nextAppointment.date, NEXT_VISIT_FORMAT)}
                </span>
                {nextAppointment.type ? ` — ${nextAppointment.type}` : ''}
              </Typography>
            </Box>
          )}
        </Box>

        {request.rawIntakeBody && (
          <Box sx={{ p: 1.5, bgcolor: 'grey.100', borderRadius: 1, mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Original message
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>
              {request.rawIntakeBody}
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Action selector */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Decision
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button
            variant={action === 'approve' ? 'contained' : 'outlined'}
            color="success"
            startIcon={<ApproveIcon />}
            onClick={() => setAction('approve')}
            disabled={!med || fillsLeft <= 0}
            sx={{ textTransform: 'none' }}
          >
            Approve
          </Button>
          <Button
            variant={action === 'recheck' ? 'contained' : 'outlined'}
            color="warning"
            startIcon={<RecheckIcon />}
            onClick={() => setAction('recheck')}
            sx={{ textTransform: 'none' }}
          >
            Request recheck
          </Button>
          <Button
            variant={action === 'deny' ? 'contained' : 'outlined'}
            color="error"
            startIcon={<DenyIcon />}
            onClick={() => setAction('deny')}
            sx={{ textTransform: 'none' }}
          >
            Deny
          </Button>
        </Stack>

        {action === 'approve' && fillsLeft <= 0 && med && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            All authorized fills have been dispensed for this Rx. A new prescription is required.
          </Alert>
        )}

        {action === 'approve' && med && fillsLeft > 0 && (
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={dispenseNow}
                  onChange={(e) => setDispenseNow(e.target.checked)}
                  disabled={submitting}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <DispenseIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="body2">
                    Dispense now and record the fill
                  </Typography>
                </Box>
              }
            />
            {dispenseNow && (
              <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 2, mt: 1, ml: 4 }}>
                <TextField
                  label="Quantity"
                  type="number"
                  size="small"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  inputProps={{ min: 0.001, step: 'any' }}
                  disabled={submitting}
                  fullWidth
                />
                <TextField
                  label="Unit"
                  size="small"
                  value={unit}
                  onChange={(e) => setUnit(e.target.value)}
                  placeholder="e.g. mL, tab"
                  disabled={submitting}
                  fullWidth
                />
              </Box>
            )}
          </Box>
        )}

        <TextField
          label="Decision notes (optional)"
          multiline
          rows={2}
          size="small"
          fullWidth
          value={decisionNotes}
          onChange={(e) => setDecisionNotes(e.target.value)}
          disabled={submitting}
          placeholder={
            action === 'approve'
              ? 'e.g. Continue current dose, follow up in 4 weeks'
              : action === 'deny'
                ? 'e.g. Out of refills; schedule exam first'
                : 'e.g. Owner reports new symptoms — bring in for evaluation'
          }
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={action === 'deny' ? 'error' : action === 'approve' ? 'success' : 'warning'}
          onClick={handleSubmit}
          disabled={submitting || (action === 'approve' && !med)}
          startIcon={
            submitting ? (
              <CircularProgress size={16} color="inherit" />
            ) : action === 'approve' ? (
              <ApproveIcon />
            ) : action === 'deny' ? (
              <DenyIcon />
            ) : (
              <RecheckIcon />
            )
          }
        >
          {submitting
            ? 'Saving…'
            : action === 'approve'
              ? dispenseNow
                ? 'Approve & dispense'
                : 'Approve'
              : action === 'deny'
                ? 'Deny refill'
                : 'Request recheck'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RefillRequestDecisionDialog;
